import React, { useEffect, useRef } from 'react';
import { Bot, X } from 'lucide-react';
import { AWiseWordmark } from '../branding/index.tsx';
import ChatBotBubbleContent from './ChatBotBubbleContent.tsx';

export interface ChatMessage {
  sender: 'user' | 'bot';
  text: string;
}

interface AgedCareWiseAiChatPanelProps {
  open: boolean;
  onClose: () => void;
  messages: ChatMessage[];
  chatInput: string;
  onChatInputChange: (value: string) => void;
  onSubmit: () => void;
  sending?: boolean;
  onSubscribe?: () => void;
  subscribeLabel?: string;
  className?: string;
}

/**
 * Floating chat panel for AWise (landing or in-app).
 */
export default function AgedCareWiseAiChatPanel({
  open,
  onClose,
  messages,
  chatInput,
  onChatInputChange,
  onSubmit,
  sending,
  onSubscribe,
  subscribeLabel = 'Get Started',
  className = '',
}: AgedCareWiseAiChatPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open, sending]);

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-end justify-center sm:justify-end p-4 pointer-events-none ${className}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="awise-chat-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/30 pointer-events-auto"
        aria-label="Close chat"
        onClick={onClose}
      />
      <div className="relative pointer-events-auto w-full max-w-md bg-white rounded-2xl shadow-2xl flex flex-col max-h-[min(32rem,85vh)] mb-2 sm:mb-10 sm:mr-4">
        <div className="flex justify-between items-center gap-2 p-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Bot className="text-blue-600 shrink-0" aria-hidden="true" />
            <AWiseWordmark id="awise-chat-title" size="lg" />
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {onSubscribe && (
              <button
                type="button"
                onClick={onSubscribe}
                className="text-xs sm:text-sm font-semibold text-blue-600 hover:text-blue-800 px-2 py-1 rounded-lg hover:bg-blue-50"
              >
                {subscribeLabel}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="overflow-y-auto flex-1 p-4 space-y-3 min-h-[12rem]">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`px-3 py-2 rounded-lg max-w-[92%] ${
                  msg.sender === 'user' ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {msg.sender === 'user' ? (
                  <span className="text-sm whitespace-pre-line">{msg.text}</span>
                ) : (
                  <ChatBotBubbleContent text={msg.text} />
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="px-3 py-2 rounded-lg bg-gray-100 text-gray-500 text-sm">Thinking…</div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 shrink-0">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              className="flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
              placeholder="What's on your mind?"
              value={chatInput}
              onChange={(e) => onChatInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.shiftKey) return;
                e.preventDefault();
                onSubmit();
              }}
              disabled={sending}
              aria-label="Chat message"
            />
            <button
              type="button"
              className="shrink-0 bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-60"
              onClick={onSubmit}
              disabled={sending || !chatInput.trim()}
            >
              {sending ? '…' : 'Send'}
            </button>
          </div>
          <p className="mt-2 text-[11px] text-gray-500 leading-snug">
            General information only — not personal advice. Personalised modelling is available after you subscribe.
          </p>
        </div>
      </div>
    </div>
  );
}
